import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { usePlan } from '../context/PlanContext';
import { Check, X, Crown, Zap, Sparkles, Building2 } from 'lucide-react';

const PLAN_ICONS = {
    free: Zap,
    starter: Sparkles,
    pro: Crown,
    enterprise: Building2,
};

const PLAN_ACCENT = {
    free: 'border-stone-200',
    starter: 'border-blue-400',
    pro: 'border-stone-900 ring-2 ring-stone-900/10',
    enterprise: 'border-amber-400',
};

const PLAN_BTN = {
    free: 'bg-stone-100 text-stone-600 cursor-default',
    starter: 'bg-blue-600 text-white hover:bg-blue-700',
    pro: 'bg-stone-900 text-white hover:bg-stone-800',
    enterprise: 'bg-amber-600 text-white hover:bg-amber-700',
};

const FEATURE_LIST = [
    { key: 'ai_assistant', label: 'AI Sales Assistant' },
    { key: 'ai_heartbeat', label: 'AI Heartbeat Automations' },
    { key: 'email_sequences', label: 'Email Sequences' },
    { key: 'deal_rooms', label: 'Deal Rooms' },
    { key: 'booking_links', label: 'Booking Links' },
    { key: 'list_management', label: 'List Management' },
    { key: 'custom_domains', label: 'Custom Domains' },
    { key: 'proposals', label: 'Proposals' },
    { key: 'reports', label: 'Reports & Analytics' },
];

export default function UpgradeModal({ open, onClose, triggerResource, triggerFeature }) {
    const { plan, tiers } = usePlan();
    const [selectedTier, setSelectedTier] = useState(null);

    if (!tiers) return null;

    const tierOrder = ['free', 'starter', 'pro', 'enterprise'];
    const currentIdx = tierOrder.indexOf(plan);

    const triggerLabel = triggerResource
        ? triggerResource.replace(/_/g, ' ')
        : triggerFeature
            ? triggerFeature.replace(/_/g, ' ')
            : '';

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="upgrade-modal">
                <div className="p-6 pb-2">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-stone-900">
                            {triggerLabel ? `Upgrade to unlock ${triggerLabel}` : 'Choose Your Plan'}
                        </DialogTitle>
                        <DialogDescription>
                            {triggerLabel
                                ? `Your current ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan has reached its limit. Upgrade for more capacity and features.`
                                : 'Compare plans and find the right fit for your team.'}
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 pt-2">
                    {tierOrder.map((tierKey) => {
                        const tier = tiers[tierKey];
                        if (!tier) return null;
                        const Icon = PLAN_ICONS[tierKey] || Zap;
                        const isCurrent = tierKey === plan;
                        const isUpgrade = tierOrder.indexOf(tierKey) > currentIdx;
                        const isPopular = tierKey === 'pro';

                        return (
                            <div
                                key={tierKey}
                                className={`relative rounded-xl border-2 p-5 flex flex-col transition-all ${PLAN_ACCENT[tierKey]} ${isCurrent ? 'bg-stone-50' : 'bg-white hover:shadow-md'}`}
                                data-testid={`plan-${tierKey}`}
                            >
                                {isPopular && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-stone-900 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                        Most Popular
                                    </span>
                                )}
                                <div className="flex items-center gap-2 mb-3">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCurrent ? 'bg-stone-200' : 'bg-stone-100'}`}>
                                        <Icon className="w-4 h-4 text-stone-700" />
                                    </div>
                                    <h3 className="font-bold text-stone-900">{tier.label}</h3>
                                </div>
                                <div className="mb-3">
                                    <span className="text-3xl font-bold text-stone-900">${tier.price}</span>
                                    <span className="text-sm text-stone-500">/mo per user</span>
                                </div>
                                <p className="text-xs text-stone-500 mb-4">{tier.description}</p>

                                <div className="space-y-2 mb-5 flex-1">
                                    <p className="text-xs font-semibold text-stone-500 uppercase">Limits</p>
                                    <div className="text-xs text-stone-600 space-y-1">
                                        <div>Users: {tier.limits.users === -1 ? 'Unlimited' : tier.limits.users}</div>
                                        <div>Contacts: {tier.limits.contacts === -1 ? 'Unlimited' : tier.limits.contacts.toLocaleString()}</div>
                                        <div>Accounts: {tier.limits.accounts === -1 ? 'Unlimited' : tier.limits.accounts.toLocaleString()}</div>
                                        <div>Deals: {tier.limits.deals === -1 ? 'Unlimited' : tier.limits.deals.toLocaleString()}</div>
                                    </div>

                                    <p className="text-xs font-semibold text-stone-500 uppercase pt-2">Features</p>
                                    <div className="space-y-1">
                                        {FEATURE_LIST.map(f => {
                                            const enabled = tier.features[f.key];
                                            return (
                                                <div key={f.key} className="flex items-center gap-1.5 text-xs">
                                                    {enabled
                                                        ? <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                                                        : <X className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />}
                                                    <span className={enabled ? 'text-stone-700' : 'text-stone-400'}>{f.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <Button
                                    className={`w-full text-sm ${isCurrent ? 'bg-stone-100 text-stone-600 cursor-default hover:bg-stone-100' : PLAN_BTN[tierKey]}`}
                                    disabled={!isUpgrade}
                                    onClick={() => isUpgrade && setSelectedTier(tierKey)}
                                    data-testid={`select-plan-${tierKey}`}
                                >
                                    {isCurrent ? 'Current Plan' : isUpgrade ? `Upgrade to ${tier.label}` : tier.label}
                                </Button>
                            </div>
                        );
                    })}
                </div>

                {selectedTier && (
                    <div className="mx-6 mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="upgrade-contact-msg">
                        <p className="text-sm text-amber-800 font-medium">
                            To upgrade to the {tiers[selectedTier]?.label} plan, please contact your platform administrator.
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                            Plan changes are managed by the super admin. They can update your organization's plan from the Platform Administration dashboard.
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
